import{ useState, useEffect } from "react";
import { Form, Button, Alert } from "react-bootstrap";
import { IconPicker } from "utils/fontawesome";

function CategoryForm(props) {
  const { name, icon, onChange } = props;
  const [iconType, setIconType] = useState(() => {
    // Determine initial icon type based on current icon value
    return icon && icon.startsWith('data:image') ? 'custom' : 'fontawesome';
  });
  const [customIconFile, setCustomIconFile] = useState(null);
  const [customIconPreview, setCustomIconPreview] = useState(null);
  const [uploadError, setUploadError] = useState("");

  // Update state when icon prop changes (e.g., when editing existing model)
  useEffect(() => {
    if (icon) {
      if (icon.startsWith('data:image')) {
        setIconType('custom');
        setCustomIconPreview(icon);
        setCustomIconFile(null); // We don't have the original file, just the base64
      } else {
        setIconType('fontawesome');
        setCustomIconPreview(null);
        setCustomIconFile(null);
      }
    } else {
      setIconType('fontawesome');
      setCustomIconPreview(null);
      setCustomIconFile(null);
    }
  }, [icon]);

  // Store previous values when switching between icon types
  const [previousFontAwesomeIcon, setPreviousFontAwesomeIcon] = useState("");
  const [previousCustomIcon, setPreviousCustomIcon] = useState("");

  const handleIconTypeChange = (type) => {
    if (type === 'fontawesome') {
      // Store current custom icon before switching
      if (iconType === 'custom' && icon) {
        setPreviousCustomIcon(icon);
      }
      // Restore previous FontAwesome icon or clear if none
      setIconType('fontawesome');
      setCustomIconPreview(null);
      setCustomIconFile(null);
      setUploadError("");
      onChange({ icon: previousFontAwesomeIcon || "" });
    } else {
      // Store current FontAwesome icon before switching
      if (iconType === 'fontawesome' && icon) {
        setPreviousFontAwesomeIcon(icon);
      }
      // Restore previous custom icon or clear if none
      setIconType('custom');
      setCustomIconPreview(previousCustomIcon || null);
      setCustomIconFile(null);
      setUploadError("");
      onChange({ icon: previousCustomIcon || "" });
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    setUploadError("");
    
    if (!file) {
      setCustomIconFile(null);
      setCustomIconPreview(null);
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setUploadError("Please select an image file (PNG, JPG, SVG, etc.)");
      setCustomIconFile(null);
      setCustomIconPreview(null);
      return;
    }

    // Validate file size (max 1MB for Firestore compatibility)
    if (file.size > 1 * 1024 * 1024) {
      setUploadError("File size must be less than 1MB");
      setCustomIconFile(null);
      setCustomIconPreview(null);
      return;
    }

    setCustomIconFile(file);

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setCustomIconPreview(e.target.result);
    };
    reader.readAsDataURL(file);

    // Convert to base64 and update the icon value
    const base64Reader = new FileReader();
    base64Reader.onload = (e) => {
      const base64String = e.target.result;
      onChange({ icon: base64String });
    };
    base64Reader.readAsDataURL(file);
  };

  const clearCustomIcon = () => {
    setCustomIconFile(null);
    setCustomIconPreview(null);
    setUploadError("");
    onChange({ icon: "" });
  };

  return (
    <>
      <Form>
        <Form.Group className="mb-3" controlId="name">
          <Form.Label>Name</Form.Label>
          <Form.Control
            value={name}
            onChange={(e) => props.onChange({ name: e.target.value })}
            type="text"
            placeholder="Name of the category"
          />
        </Form.Group>

        {/* Icon Selection Section */}
        <Form.Group className="mb-3" controlId="icon">
          <Form.Label>Icon</Form.Label>
          
          {/* Icon Type Selection */}
          <div className="mb-3">
            <Form.Check
              type="radio"
              id="fontawesome-radio"
              name="iconType"
              label="Choose from FontAwesome icons"
              checked={iconType === 'fontawesome'}
              onChange={() => handleIconTypeChange('fontawesome')}
              className="mb-2"
            />
            <Form.Check
              type="radio"
              id="custom-radio"
              name="iconType"
              label="Upload custom icon"
              checked={iconType === 'custom'}
              onChange={() => handleIconTypeChange('custom')}
            />
          </div>

          {/* FontAwesome Icon Picker */}
          {iconType === 'fontawesome' && (
            <div className="border rounded p-3 bg-light">
              <Form.Control
                type="text"
                placeholder="Font Awesome Icon Name"
                value={icon && !icon.startsWith('data:image') ? icon : ''}
                onChange={(e) => onChange({ icon: e.target.value })}
                className="mb-2"
              />
              <Form.Text className="d-block text-muted mb-2">
                <small>
                  Browse icons at{" "}
                  <a href="http://fontawesome.com/search?o=r&m=free&s=solid&f=classic" target="_blank" rel="noopener noreferrer">
                    FontAwesome
                  </a>
                </small>
              </Form.Text>
              <IconPicker
                value={icon && !icon.startsWith('data:image') ? icon : ''}
                onChange={(v) => onChange({ icon: v })}
              />
            </div>
          )}

          {/* Custom Icon Upload */}
          {iconType === 'custom' && (
            <div className="border rounded p-3 bg-light">
              <div className="d-flex align-items-center gap-2 mb-2">
                <Form.Control
                  type="file"
                  accept="image/*"
                  onChange={handleFileUpload}
                  className="flex-grow-1"
                />
                {customIconFile && (
                  <Button 
                    variant="outline-danger" 
                    size="sm" 
                    onClick={clearCustomIcon}
                  >
                    Clear
                  </Button>
                )}
              </div>
              
              {uploadError && (
                <Alert variant="danger" className="py-2">
                  {uploadError}
                </Alert>
              )}

              {customIconPreview && (
                <div className="mt-2">
                  <Form.Label className="d-block">Preview:</Form.Label>
                  <div className="border rounded p-2 d-inline-block bg-white">
                    <img 
                      src={customIconPreview} 
                      alt="Custom icon preview" 
                      style={{ 
                        maxWidth: '32px', 
                        maxHeight: '32px',
                        objectFit: 'contain'
                      }} 
                    />
                  </div>
                  <Form.Text className="d-block text-muted">
                    <small>Icon will be stored as base64 in the model</small>
                  </Form.Text>
                </div>
              )}

              <Form.Text>
                <small className="text-muted">
                  Supported formats: PNG, JPG, SVG, GIF. Max size: 1MB.
                </small>
              </Form.Text>
            </div>
          )}
        </Form.Group>
      </Form>
    </>
  );
}

export default CategoryForm;
